import i18next, { type Resource, type ResourceLanguage, type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

import type { PublishedLocaleManifest } from '@/i18n/types';

/**
 * Phase 6 — synchronous i18n initialiser.
 *
 * Mirrors `createPublishedI18n` (same `lng`, `fallbackLng`, `supportedLngs`,
 * `ns`, `defaultNS`, `interpolation` config) but takes a pre-loaded bundle of
 * resources instead of fetching namespace files asynchronously. This is what
 * the root loader hands to `LocaleProvider`, so i18next is ready on the very
 * first render and prerendered HTML hydrates without a mismatch.
 *
 * We cache a shared instance keyed by language so re-renders don't pay to
 * spin up i18next repeatedly.
 */
let sharedI18nInstance: I18nInstance | null = null;
let sharedI18nLanguage: string | null = null;

export function createPublishedI18nSync(
    manifest: PublishedLocaleManifest,
    locale: string,
    bundle: Record<string, Record<string, unknown>>,
): I18nInstance {
    const resolvedLocale = resolveSupportedLocale(manifest, locale);

    if (sharedI18nInstance && sharedI18nLanguage === resolvedLocale) {
        return sharedI18nInstance;
    }

    const resources = buildSyncResources(manifest, resolvedLocale, bundle);
    const instance = i18next.createInstance();

    instance.use(initReactI18next);

    // Synchronous init — no `i18next-http-backend`, no awaiting; resources are
    // already in memory courtesy of the root loader.
    void instance.init({
        lng: resolvedLocale,
        fallbackLng: manifest.defaultLocale,
        supportedLngs: manifest.locales.map(item => item.code),
        ns: manifest.namespaces,
        defaultNS: manifest.namespaces[0],
        resources,
        interpolation: {
            escapeValue: false,
        },
        react: {
            useSuspense: false,
        },
        initImmediate: false,
    });

    sharedI18nInstance = instance;
    sharedI18nLanguage = resolvedLocale;
    return instance;
}

function buildSyncResources(
    manifest: PublishedLocaleManifest,
    locale: string,
    bundle: Record<string, Record<string, unknown>>,
): Resource {
    const resources: Resource = {};

    for (const localeEntry of manifest.locales) {
        const code = localeEntry.code;
        if (bundle[code]) {
            resources[code] = bundle[code] as ResourceLanguage;
        }
    }

    // Make sure the resolved locale and default locale both have an entry —
    // even if empty — so i18next does not warn about a missing namespace.
    if (!resources[locale]) {
        resources[locale] = {} as ResourceLanguage;
    }
    if (!resources[manifest.defaultLocale]) {
        resources[manifest.defaultLocale] = {} as ResourceLanguage;
    }

    return resources;
}

export async function createPublishedI18n(
    manifest: PublishedLocaleManifest,
    locale: string,
): Promise<I18nInstance> {
    const resolvedLocale = resolveSupportedLocale(manifest, locale);
    const resources = await loadPublishedResources(
        manifest.namespaces,
        resolvedLocale,
        manifest.defaultLocale,
        manifest.snapshotVersion,
    );
    const instance = i18next.createInstance();

    instance.use(initReactI18next);

    await instance.init({
        lng: resolvedLocale,
        fallbackLng: manifest.defaultLocale,
        supportedLngs: manifest.locales.map(item => item.code),
        ns: manifest.namespaces,
        defaultNS: manifest.namespaces[0],
        resources,
        interpolation: {
            escapeValue: false,
        },
    });

    return instance;
}

async function loadPublishedResources(
    namespaces: string[],
    locale: string,
    defaultLocale: string,
    snapshotVersion: string,
): Promise<Resource> {
    const entries = await Promise.all(
        namespaces.map(async namespace => {
            const payload = await loadNamespacePayload(locale, namespace, snapshotVersion).catch(() => null);
            const resolvedPayload = payload ?? await loadFallbackNamespace(locale, defaultLocale, namespace, snapshotVersion);

            return [namespace, resolvedPayload] as const;
        }),
    );

    return {
        [locale]: Object.fromEntries(entries) as ResourceLanguage,
    };
}

async function loadFallbackNamespace(
    locale: string,
    defaultLocale: string,
    namespace: string,
    snapshotVersion: string,
): Promise<unknown> {
    if (locale === defaultLocale) {
        throw new Error(`Failed to load locale namespace: ${locale}/${namespace}`);
    }

    const fallbackResponse = await fetch(
        `/locales/${defaultLocale}/${namespace}.json?v=${encodeURIComponent(snapshotVersion)}`,
    );

    if (!fallbackResponse.ok) {
        throw new Error(`Failed to load locale namespace: ${defaultLocale}/${namespace}`);
    }

    return fallbackResponse.json();
}

async function loadNamespacePayload(
    locale: string,
    namespace: string,
    snapshotVersion: string,
): Promise<unknown | null> {
    const response = await fetch(`/locales/${locale}/${namespace}.json?v=${encodeURIComponent(snapshotVersion)}`);

    if (!response.ok) {
        return null;
    }

    return response.json();
}

function resolveSupportedLocale(manifest: PublishedLocaleManifest, locale: string): string {
    const normalizedLocale = locale.trim().toLowerCase();

    return manifest.locales.some(item => item.code === normalizedLocale)
        ? normalizedLocale
        : manifest.defaultLocale;
}
